import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { getCanonical } from 'services/datachecks/datachecks';
import { queryClient } from 'services/http/queryClient';
import { logError } from 'services/logging/logging';
import { useMessageWithProcessSpecification, useUpdateMessage } from 'services/repositories/messages/messages';
import { addNotification, NotificationType } from 'stores/notifications/notifications';
import { ConflictError } from 'support/helpers/errors/errors';
import { routeToTransactionPage } from 'support/helpers/navigation/navigation';
import { enrichDocument } from 'support/helpers/transformer/transformer';
import { DocumentType, MessageV2DTO, ProcessSpecificationDTO, TransactionInternalType } from 'support/types';

import { removeInternalKeys } from '../utils';

export function useEditMessage({
  message,
  processSpecificationFields,
}: {
  message: MessageV2DTO | undefined;
  processSpecificationFields: ProcessSpecificationDTO['fields'] | undefined;
}) {
  const navigate = useNavigate();
  const { mutate: mutateUpdateMessage, isSuccess, error } = useUpdateMessage();
  const { t } = useTranslation();

  const [isEditingMessage, setIsEditingMessage] = useState(false);
  const editMessage = useCallback(
    async (formValues: any) => {
      if (isEditingMessage) {
        logError('Already editing message');
        return;
      }
      if (!message) {
        logError('No message found to edit');
        return;
      }
      setIsEditingMessage(true);
      if (!processSpecificationFields) {
        logError('No process specification found');
        setIsEditingMessage(false);
        return;
      }

      try {
        const { canonical } = await getCanonical(processSpecificationFields, formValues);
        const { data: enrichedCanonical } = enrichDocument({
          messageType: message.type as DocumentType,
          data: removeInternalKeys(canonical),
        });

        console.log('Enriched canonical for message edit', enrichedCanonical);

        mutateUpdateMessage(
          {
            messageId: message.id,
            data: {
              data: enrichedCanonical,
              message_update_key: message.updated_at,
            },
          },
          {
            onSuccess: () => {
              navigate(routeToTransactionPage(TransactionInternalType.message, message.id));
              queryClient.resetQueries(useMessageWithProcessSpecification.getKey({ messageId: message.id }));
            },
            onError: (err) => {
              logError(err);
              if (err instanceof ConflictError) {
                addNotification(
                  {
                    title: t('webedi:editDocument.failedToast.title'),
                    subtitle: t('webedi:editDocument.failedToast.subtitle'),
                  },
                  NotificationType.error,
                );
              } else {
                addNotification(
                  {
                    title: t('webedi:editDocument.failedToastGeneric.title'),
                  },
                  NotificationType.error,
                );
              }
            },
            onSettled: () => {
              setIsEditingMessage(false);
            },
          },
        );
      } catch (error) {
        logError(error);
        addNotification(
          {
            title: t('common:error.generic'),
          },
          NotificationType.error,
        );
        setIsEditingMessage(false);
      }
    },
    [processSpecificationFields, mutateUpdateMessage, message, t, navigate, isEditingMessage],
  );

  return { editMessage, isUpdating: isEditingMessage, isSuccess, error };
}
